//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import ArrayUtil from 'common/utils/ArrayUtil';

const recordsActionTypes = Object.freeze({
    SET_RECORDS: 'SET_RECORDS',
    FILTER_RECORDS: 'FILTER_RECORDS',
    TOGGLE_ROW_SELECTION: 'TOGGLE_ROW_SELECTION',
    TOGGLE_SELECTION_ALL: 'TOGGLE_SELECTION_ALL',
    TOGGLE_SELECTION_PAGE: 'TOGGLE_SELECTION_PAGE'
});

type ActionTypes = $Values<typeof recordsActionTypes>;

type FiltersType = Array<{
    id: string,
    value: string | { values: Array<string>, allowEmptyOrNull: boolean }
}>;

type PaginationInfoDataType = {
    page: number,
    numberOfPages: number,
    size: number
};

type EntitiesDataTypes = {
    entities: Array<Immutable.Map<*, *>>,
    paginationInfo: PaginationInfoDataType,
    selectedRowIds: Array<string>
};

type StateType = {
    allRecords: ?Array<Immutable.Map<*, *>>,
    data: EntitiesDataTypes,
    filters: FiltersType
};

const getPaginatedData = (
    records: Array<Immutable.Map<*, *>>,
    metaData: {
        page: number,
        size: number
    }
): {
    entities: Array<Immutable.Map<*, *>>,
    paginationInfo: {
        page: number,
        size: number,
        numberOfPages: number
    }
} => {
    const { page, size } = metaData;
    const from = (page - 1) * size;
    const to = page * size;
    return {
        entities: records.slice(from, to),
        paginationInfo: {
            ...metaData,
            numberOfPages: Math.ceil(records.length / (size || 15))
        }
    };
};

const incrementalRecordsReducer = (
    state: StateType,
    action: {
        type: ActionTypes,
        payload: any
    }
): StateType => {
    switch (action.type) {
        case recordsActionTypes.SET_RECORDS: {
            const { entities, EntitiesModel } = action.payload;

            const allRecords = entities.map(outlet =>
                EntitiesModel.fromJS(outlet)
            );

            const paginationInfo = {
                size: 15,
                currentPage: 1
            };
            const data = getPaginatedData(allRecords, {
                page: paginationInfo.currentPage,
                size: paginationInfo.size
            });

            return {
                ...state,
                allRecords,
                data: {
                    ...state.data,
                    ...data,
                    selectedRowIds: state.data.selectedRowIds
                }
            };
        }

        case recordsActionTypes.FILTER_RECORDS: {
            const { allRecords } = state;
            const { pageSize, page, filtered } = action.payload;

            let records = [...(allRecords || [])];
            if (filtered.length) {
                const filters = filtered.reduce((filters, filter) => {
                    if (filter.value) {
                        filters[filter.id] = filter.value;
                    }
                    return filters;
                }, {});
                if (Object.keys(filters).length) {
                    records = allRecords?.filter(record => {
                        let doesFiltersMatch = false;
                        if (
                            filters.code &&
                            record.code
                                .toLowerCase()
                                .startsWith(filters.code.toLowerCase().trim())
                        ) {
                            doesFiltersMatch = true;
                        }
                        if (
                            filters.name &&
                            record.name
                                .toLowerCase()
                                .startsWith(filters.name.toLowerCase().trim())
                        ) {
                            doesFiltersMatch = true;
                        }
                        if (filters['teams.name']?.values.length) {
                            doesFiltersMatch = filters[
                                'teams.name'
                            ].values.every(team =>
                                record.teams.find(
                                    ({ teamId }) => teamId === team
                                )
                            );
                        }
                        if (filters['teams.id']?.values.length) {
                            doesFiltersMatch = filters[
                                'teams.id'
                            ].values.every(team =>
                                record.teams.find(
                                    ({ teamId }) => teamId === team
                                )
                            );
                        }
                        return doesFiltersMatch;
                    });
                }
            }

            const paginationInfo = {
                size: pageSize,
                currentPage: page + 1
            };
            const data = getPaginatedData(records || [], {
                page: paginationInfo.currentPage,
                size: paginationInfo.size
            });

            return {
                ...state,
                data: {
                    ...state.data,
                    ...data,
                    selectedRowIds: []
                }
            };
        }

        case recordsActionTypes.TOGGLE_ROW_SELECTION: {
            const { id, selected } = action.payload;
            let selectedRowIds = [...state.data.selectedRowIds];
            if (selected) {
                selectedRowIds.push(id);
            } else {
                selectedRowIds = ArrayUtil.removeItemAtIndex({
                    array: selectedRowIds,
                    index: selectedRowIds.indexOf(id)
                });
            }
            return {
                ...state,
                data: {
                    ...state.data,
                    selectedRowIds
                }
            };
        }

        case recordsActionTypes.TOGGLE_SELECTION_ALL: {
            const { selected } = action.payload;
            return {
                ...state,
                data: {
                    ...state.data,
                    selectedRowIds: selected
                        ? state.allRecords.map(({ id }) => id)
                        : []
                }
            };
        }

        case recordsActionTypes.TOGGLE_SELECTION_PAGE: {
            return {
                ...state,
                data: {
                    ...state.data,
                    selectedRowIds: state.data.entities.map(({ id }) => id)
                }
            };
        }

        default:
            return state;
    }
};

const useIncrementalState = ({
    fetchRecords,
    EntitiesModel
}: {
    fetchRecords: () => Promise<void>,
    EntitiesModel: Immutable.Map<*, *>
}): ({
    data: EntitiesDataTypes,
    onTableStateChange: ({
        pageSize: number,
        page: number,
        sorted: Array<Object>,
        filtered: Array<{ id: string, value: string }>
    }) => void,
    isLoading: boolean,
    totalCount: ?number,
    allRecords: Array<Immutable.Map<*, *>>,
    toggleRowSelection: (id: string, checked: boolean) => void,
    toggleSelectAll: (checked: boolean) => void,
    toggleSelectPage: (checked: boolean) => void
}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [{ data, allRecords }, dispatch] = React.useReducer(
        incrementalRecordsReducer,
        {
            allRecords: null,
            data: {
                selectedRowIds: [],
                entities: [],
                paginationInfo: {}
            },
            filters: []
        }
    );

    const filterRecords = payload =>
        dispatch({
            type: recordsActionTypes.FILTER_RECORDS,
            payload
        });

    const setRecords = React.useCallback(
        payload =>
            dispatch({
                type: recordsActionTypes.SET_RECORDS,
                payload: {
                    ...payload,
                    EntitiesModel
                }
            }),
        [EntitiesModel]
    );

    const onTableStateChange = React.useCallback(
        (tableState: {
            pageSize: number,
            page: number,
            sorted: Array<Object>,
            filtered: Array<{ id: string, value: string }>
        }): void => {
            if (!isLoading) {
                if (!allRecords) {
                    setIsLoading(true);
                    fetchRecords().then(entities => {
                        setRecords({ entities });
                        setIsLoading(false);
                    });
                } else {
                    filterRecords(tableState);
                }
            }
        },
        [allRecords, fetchRecords, isLoading, setRecords]
    );

    const toggleRowSelection = React.useCallback((id, selected) => {
        dispatch({
            type: recordsActionTypes.TOGGLE_ROW_SELECTION,
            payload: { selected, id }
        });
    }, []);

    const toggleSelectAll = React.useCallback(selected => {
        dispatch({
            type: recordsActionTypes.TOGGLE_SELECTION_ALL,
            payload: {
                selected
            }
        });
    }, []);

    const toggleSelectPage = React.useCallback(selected => {
        dispatch({
            type: recordsActionTypes.TOGGLE_SELECTION_PAGE,
            payload: {
                selected
            }
        });
    }, []);

    return {
        onTableStateChange,
        data,
        isLoading,
        allRecords,
        totalCount: allRecords?.length,
        toggleRowSelection,
        toggleSelectAll,
        toggleSelectPage
    };
};

export default useIncrementalState;
